import React, { useEffect, useRef, useState } from "react"
import { AssistantStream } from "openai/lib/AssistantStream"
import { createThread, sendThreadMessage, deleteThread } from "../services/aiTryService"

// Extract a ```json [...] ``` product block from an assistant message.
const parseMessageContent = message => {
  const match = message.match(/```json\s*([\s\S]*?)\s*```/)
  let textContent = message
  let products = []
  if (match) {
    try {
      products = JSON.parse(match[1])
      textContent = message.replace(match[0], "").trim()
    } catch (e) {
      console.error("Error parsing product JSON:", e)
    }
  }
  return { textContent, products }
}

const ProductCard = ({ product }) => (
  <a href={product?.product_url} target="_blank" rel="noopener noreferrer" className="aitry-product">
    <img src={product.image} alt={product.title} className="aitry-product__img" />
    <h4 className="aitry-product__title">{product.title}</h4>
    <p className="aitry-product__desc">{product.description}</p>
    <p className="aitry-product__price">{product.price}</p>
  </a>
)

const AssistantMessage = ({ text }) => {
  const { textContent, products } = parseMessageContent(text)
  return (
    <div className="aitry-msg aitry-msg--assistant">
      <p className="aitry-msg__text">{textContent}</p>
      {products.length > 0 && (
        <div className="aitry-product-grid">
          {products.map((p, i) => (
            <ProductCard key={i} product={p} />
          ))}
        </div>
      )}
    </div>
  )
}

const Message = ({ role, text }) => {
  if (role === "user") return <div className="aitry-msg aitry-msg--user">{text}</div>
  if (role === "assistant") return <AssistantMessage text={text} />
  if (role === "loading")
    return (
      <div className="aitry-msg aitry-msg--assistant">
        <span className="aitry-loader" />
      </div>
    )
  return null
}

const AIChatWindow = ({ name }) => {
  const [userInput, setUserInput] = useState("")
  const [messages, setMessages] = useState([])
  const [inputDisabled, setInputDisabled] = useState(true)
  const messagesEndRef = useRef(null)
  const threadIdRef = useRef("")

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  useEffect(() => { scrollToBottom() }, [messages])

  // Create thread on mount; delete on unmount.
  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        const threadId = await createThread()
        if (!mounted) return
        threadIdRef.current = threadId
        const response = await sendThreadMessage(threadId, { content: "Introduce Yourself", path: name })
        handleReadableStream(AssistantStream.fromReadableStream(response.body))
        setInputDisabled(false)
      } catch (e) {
        console.error("Failed to start chat:", e)
      }
    })()
    return () => {
      mounted = false
      if (threadIdRef.current) deleteThread(threadIdRef.current).catch(() => {})
    }
  }, []) // eslint-disable-line

  const sendMessage = async text => {
    const response = await sendThreadMessage(threadIdRef.current, { content: text, path: name })
    handleReadableStream(AssistantStream.fromReadableStream(response.body))
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (!userInput.trim()) return
    sendMessage(userInput)
    setMessages(prev => [...prev, { role: "user", text: userInput }])
    setUserInput("")
    setInputDisabled(true)
    scrollToBottom()
  }

  /* Stream handlers */
  const appendToLastMessage = text =>
    setMessages(prev => {
      const last = prev[prev.length - 1]
      return [...prev.slice(0, -1), { ...last, text: last.text + text }]
    })
  const appendMessage = (role, text) => setMessages(prev => [...prev, { role, text }])

  const handleReadableStream = stream => {
    stream.on("textCreated", () => appendMessage("assistant", ""))
    stream.on("textDelta", delta => {
      if (delta.value != null) appendToLastMessage(delta.value)
    })
    stream.on("event", event => {
      if (event.event === "thread.run.completed") setInputDisabled(false)
    })
  }

  const showMessages =
    inputDisabled && messages[messages.length - 1]?.role !== "user"
      ? messages.slice(0, -1)
      : messages

  return (
    <div className="aitry-chat">
      <div className="aitry-chat__messages">
        {showMessages.map((msg, i) => (
          <Message key={i} role={msg.role} text={msg.text} />
        ))}
        {inputDisabled && <Message role="loading" />}
        <div ref={messagesEndRef} />
      </div>
      <form onSubmit={handleSubmit} className="aitry-chat__form">
        <div className="aitry-chat__inputrow">
          <input
            type="text"
            className="aitry-chat__input"
            value={userInput}
            onChange={e => setUserInput(e.target.value)}
            placeholder="Enter your question"
          />
          <button type="submit" className="aitry-chat__send" disabled={inputDisabled}>
            <img src="/ai/messagesend.png" alt="Send" />
          </button>
        </div>
      </form>
    </div>
  )
}

export default AIChatWindow
